// src/hooks/useInventoryTransferPermissions.ts
import { useMemo } from 'react';
import { usePermission } from './usePermission';
import { INVENTORY_PERMISSIONS } from '../constants/permissions.constants';

/**
 * Hook to get all inventory transfer-related permissions in one object
 *
 * Used for transfer operations: view, approve, reject, toggle require-counting,
 * and field visibility (total cost / sale amounts).
 */
export const useInventoryTransferPermissions = () => {
  const canView = usePermission(INVENTORY_PERMISSIONS.TRANSFER.VIEW);
  const canViewRow = usePermission(INVENTORY_PERMISSIONS.TRANSFER.VIEW_ROW);
  const canApprove = usePermission(INVENTORY_PERMISSIONS.TRANSFER.APPROVE);
  const canReject = usePermission(INVENTORY_PERMISSIONS.TRANSFER.REJECT);
  const canToggleRequireCounting = usePermission(
    INVENTORY_PERMISSIONS.TRANSFER.TOGGLE_REQUIRE_COUNTING,
  );
  const canShowTotalCostAmount = usePermission(
    INVENTORY_PERMISSIONS.TRANSFER.SHOW_TOTAL_COST_AMOUNT,
  );
  const canShowTotalSaleAmount = usePermission(
    INVENTORY_PERMISSIONS.TRANSFER.SHOW_TOTAL_SALE_AMOUNT,
  );
  /** Export CSV is part of menu view (not a separate assignable permission). */
  const canExport = canView;

  return useMemo(
    () => ({
      canView,
      canViewRow,
      canApprove,
      canReject,
      canToggleRequireCounting,
      canShowTotalCostAmount,
      canShowTotalSaleAmount,
      canExport,
    }),
    [
      canView,
      canViewRow,
      canApprove,
      canReject,
      canToggleRequireCounting,
      canShowTotalCostAmount,
      canShowTotalSaleAmount,
      canExport,
    ],
  );
};
